import * as DM from '@radix-ui/react-dropdown-menu';
import { Bell, CheckCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '@/components/ui/states';
import { cn, fmtDateTime } from '@/lib/utils';
import type { NotifyBox } from './api';
import { STATUS_LABELS, useNotifications } from './NotificationProvider';

/**
 * Chuông thông báo (P05.7): trạng thái kết nối, "Đọc hết".
 * Anh Tiến 21/10/2026: 2 tab – Cần xử lý (Khẩn + Trong ngày, số đỏ) và Bản tin (Thông tin, không số đỏ).
 */
export function NotificationBell() {
  const { unreadCount, infoUnreadCount, latest, loading, status, open, markAllRead } = useNotifications();
  const [box, setBox] = useState<NotifyBox>('action');
  const items = latest.filter((n) => (box === 'info') === (n.level === 'info')).slice(0, 20);
  const boxUnread = box === 'info' ? infoUnreadCount : unreadCount;
  return (
    <DM.Root>
      <DM.Trigger
        className="relative rounded-full p-2 text-muted-foreground outline-none hover:bg-muted hover:text-foreground"
        title={unreadCount ? `${unreadCount} thông báo chưa đọc` : 'Thông báo'}
        aria-label="Thông báo"
        data-testid="notification-bell"
      >
        <Bell className="size-5" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-unread"
            className="absolute -top-0.5 -right-0.5 flex min-w-5 items-center justify-center rounded-full bg-error px-1 text-[11px] font-bold text-white ring-2 ring-card"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </DM.Trigger>
      <DM.Portal>
        <DM.Content align="end" sideOffset={6} className="z-50 w-96 max-w-[calc(100vw-1rem)] rounded-lg border bg-card text-sm shadow-lg">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <span className="font-semibold">Thông báo</span>
            <span className={cn('text-xs', status === 'connected' ? 'text-muted-foreground' : 'text-destructive')} data-testid="realtime-status">
              · {STATUS_LABELS[status]}
            </span>
            {boxUnread > 0 && (
              <button onClick={() => void markAllRead(box)} className="ml-auto flex items-center gap-1 text-xs text-primary hover:underline">
                <CheckCheck className="size-3.5" /> Đọc hết
              </button>
            )}
          </div>
          <div className="flex gap-1 border-b p-1.5" role="tablist">
            {(
              [
                ['action', 'Cần xử lý', unreadCount],
                ['info', 'Bản tin', infoUnreadCount],
              ] as const
            ).map(([k, label, n]) => (
              <button
                key={k}
                role="tab"
                aria-selected={box === k}
                data-testid={`notification-box-${k}`}
                onClick={() => setBox(k)}
                className={cn(
                  'flex-1 rounded-md px-2 py-1 text-xs font-semibold',
                  box === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
                )}
              >
                {label}
                {n > 0 && ` · ${n > 99 ? '99+' : n}`}
              </button>
            ))}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading && !latest.length ? (
              <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
                <Spinner /> Đang tải...
              </div>
            ) : !items.length ? (
              <div className="py-6 text-center text-muted-foreground">{box === 'info' ? 'Chưa có tin' : 'Không có việc cần xử lý'}</div>
            ) : (
              items.map((n) => (
                <DM.Item
                  key={n.id}
                  onSelect={() => open(n)}
                  className={cn(
                    'flex cursor-pointer gap-2 border-b px-3 py-2 outline-none last:border-b-0 data-[highlighted]:bg-muted',
                    !n.isRead && 'bg-accent/50',
                  )}
                  data-testid="notification-item"
                >
                  <span
                    className={cn(
                      'mt-1.5 size-2 shrink-0 rounded-full',
                      n.isRead ? 'bg-transparent' : n.level === 'urgent' ? 'bg-error' : n.level === 'today' ? 'bg-warning' : 'bg-primary',
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className={cn('block', !n.isRead && 'font-semibold')}>
                      {n.level === 'urgent' && <span className="mr-1 rounded bg-error px-1 text-[10px] font-bold text-white">KHẨN</span>}
                      {n.title}
                    </span>
                    {n.escalatedAt && <span className="block text-[11px] text-warning-foreground">Quá hạn chưa ai xử lý, báo lên anh/chị</span>}
                    {n.message && <span className="block truncate text-xs text-muted-foreground">{n.message}</span>}
                    <span className="block text-[11px] text-muted-foreground">{fmtDateTime(n.createdAt)}</span>
                  </span>
                </DM.Item>
              ))
            )}
          </div>
          <DM.Item asChild>
            <Link
              to="/thong-bao"
              className="block border-t px-3 py-2 text-center text-xs text-primary outline-none hover:underline data-[highlighted]:bg-muted"
            >
              Xem tất cả thông báo
            </Link>
          </DM.Item>
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}
